/**
 * Why the runtime refused or failed a mount, as an `Error` with a closed `code`.
 *
 * `mountSurface` throws it synchronously for `host-missing` and `element-busy`;
 * `installSurfaceHost` throws it for `host-already-installed`. Every other code reaches a caller as
 * the `code` of a `surface:load-failed` runtime event, or as the rejection of
 * `loadSurfaceModule`. Callers branch on `code`, never on `instanceof`, because a page that loads
 * two copies of the runtime holds two classes.
 *
 * A message names `appId@version/surface`, or `appId@version` for a legacy bundle, at most. It
 * never carries a URL, a query string, an input, a payload or a response body.
 */
export class SurfaceRuntimeError extends Error {
    name = 'SurfaceRuntimeError';
    /** The closed reason, one of {@link SurfaceErrorCode}. */
    code;
    /**
     * `options.cause` keeps the value that made the runtime fail, as the standard `Error` `cause`.
     * The loader sets it on `load-failed` to the import error, and `mountSurface` reads it for the
     * event's {@link loadFailureMessage}. The `cause` is raw: it can hold a URL, so nothing prints it
     * as it is. Every other construction passes no `options`, and the error then has no own `cause`.
     */
    constructor(code, message, options) {
        super(message, options);
        this.code = code;
    }
}
/** The longest message a `surface:crashed` event carries (R-3). */
export const SURFACE_CRASH_MESSAGE_MAX_LENGTH = 500;
/**
 * The message a `surface:crashed` event carries for a thrown value: an `Error`'s `message`, or the
 * value as a string, cut to {@link SURFACE_CRASH_MESSAGE_MAX_LENGTH} characters (R-3).
 *
 * The one copy of the rule (R-48): the runtime calls it for every crash it contains, and
 * `@zion/sdk-surfaces-react`'s boundary imports it from `@zion/sdk-surfaces/v1` for the message it
 * hands `ctx.reportError`. An `Error` whose `message` is not a string (a number, a symbol, an
 * object) gives that message through `String()`. Never throws: a value, or a `message`, whose read,
 * string conversion or cut throws gives `'unprintable error'`, and so does a value whose prototype
 * lookup throws. Pure.
 */
export function crashMessage(thrown) {
    try {
        const raw = thrown instanceof Error ? thrown.message : thrown;
        return (typeof raw === 'string' ? raw : String(raw)).slice(0, SURFACE_CRASH_MESSAGE_MAX_LENGTH);
    }
    catch {
        return 'unprintable error';
    }
}
/** The text that {@link redactUrls} writes in place of each URL. */
export const REDACTED_URL = '<url>';
/** The characters that end a URL token: whitespace, quotes, the backtick and the bracket pairs. */
const URL_END = String.raw `\s'"\x60<>()\[\]{}`;
/**
 * One URL token, in three forms (D-3):
 * 1. a scheme with an authority, `<scheme>://...`, anywhere a scheme name can start;
 * 2. a `blob:` or a `data:` URL, at the same place;
 * 3. a protocol-relative `//...`, an absolute path `/...` or a relative path `./...` or `../...`,
 *    at the start of the text or after an end character, `=` or `:`.
 * Each form runs to the next end character and holds one character after its prefix at least.
 */
const URL_TOKEN = new RegExp(String.raw `(?<![A-Za-z0-9+.-])(?:[A-Za-z][A-Za-z0-9+.-]*:\/\/|(?:blob|data):)[^${URL_END}]+` +
    String.raw `|(?<=^|[${URL_END}=:])(?:\/\/|\.{1,2}\/|\/)[^${URL_END}]+`, 'gi');
/**
 * Replace every URL in `text` with {@link REDACTED_URL} (D-3 of the admin redesign asks, #3693).
 *
 * The runtime calls it for the `message` of a `surface:load-failed` event, which the DP shell
 * prints to the browser console. A browser names the bundle URL in an import error, and a bundle
 * URL can carry a signed-asset query, so the rule removes the whole token, query included.
 *
 * A URL token is one of:
 * - `<scheme>://` and what follows it (`http:`, `https:`, `ws:`, `file:`, any scheme, any case);
 * - `blob:` or `data:` and what follows it;
 * - `//`, `/`, `./` or `../` and what follows it, when the slash or the dot starts the text or
 *   follows whitespace, a quote, a bracket, `=` or `:`.
 * A token runs to the next whitespace, quote, backtick, `<`, `>` or bracket character. A slash with
 * no character after it (`'/'`, `a / b`) and a slash inside a word (`and/or`) stay. A regular
 * expression literal at a token start goes, as a path does: the rule removes too much before it
 * keeps a URL. A host with no scheme and no slash in front (`dp.zion.test/x`) keeps its host part.
 *
 * Accepts any string, the empty string included, and answers a string of the same text with each
 * token replaced. Never throws. Pure.
 */
export function redactUrls(text) {
    return text.replace(URL_TOKEN, REDACTED_URL);
}
/** `name: message` for a value with a string `name` and a string `message`, `message` alone for an empty `name`, else `String(value)`. */
function describeThrown(thrown) {
    if (typeof thrown === 'object' && thrown !== null) {
        const name = Reflect.get(thrown, 'name');
        const message = Reflect.get(thrown, 'message');
        if (typeof name === 'string' && typeof message === 'string')
            return name === '' ? message : `${name}: ${message}`;
    }
    return String(thrown);
}
/**
 * The `message` of a `surface:load-failed` event for the value that made a bundle import fail
 * (#3693, D-3). `mountSurface` is the caller; it passes the `cause` of the loader's `load-failed`
 * error.
 *
 * For a value with a string `name` and a string `message` (an `Error` of this realm or another, a
 * `DOMException`), the text is `name: message`, or `message` alone when `name` is empty. Any other
 * value gives `String(value)`. The text never holds a stack. {@link redactUrls} then replaces every
 * URL, and the result is cut to {@link SURFACE_CRASH_MESSAGE_MAX_LENGTH} characters, after the
 * redaction, so a cut never leaves part of a URL. Never throws: a read or a string conversion that
 * throws gives `'unprintable error'`. Pure.
 */
export function loadFailureMessage(thrown) {
    try {
        return redactUrls(describeThrown(thrown)).slice(0, SURFACE_CRASH_MESSAGE_MAX_LENGTH);
    }
    catch {
        return 'unprintable error';
    }
}
//# sourceMappingURL=errors.js.map